<template>
  <div class="shift-creation">
    <div class="header-section">
      <h2>📅 {{ $t('shiftCreation.title') }}</h2>
      <button @click="closeForm" class="close-btn">✕</button>
    </div>

    <form @submit.prevent="createShift" class="shift-form">
      <!-- Shift Time Section -->
      <div class="form-section">
        <h3>⏰ {{ $t('addWorkers.shiftTime') }}</h3>
        
        <div class="form-row">
          <div class="form-group">
            <label for="shift-date">{{ $t('shiftCreation.selectDate') }} *</label>
            <input 
              v-model="shiftForm.date" 
              id="shift-date"
              type="date" 
              required 
              class="form-input"
            />
          </div>
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="start-time">{{ $t('shiftCreation.startTime') }} *</label>
            <input 
              v-model="shiftForm.startTime" 
              id="start-time"
              type="time" 
              required 
              class="form-input"
              ref="startTimeInput"
            />
          </div>
          
          <div class="form-group">
            <label for="end-time">{{ $t('shiftCreation.endTime') }} *</label>
            <input 
              v-model="shiftForm.endTime" 
              id="end-time"
              type="time" 
              required 
              class="form-input"
            />
          </div>
        </div>

        <!-- Shift Duration Display -->
        <div v-if="shiftDuration" class="duration-display">
          <span class="duration-label">{{ $t('shiftCalendar.duration') }}:</span>
          <span class="duration-value">{{ shiftDuration }}</span>
        </div>
      </div>

      <!-- Worker Assignment Section -->
      <div class="form-section">
        <h3>👥 {{ $t('shiftCreation.selectWorkers') }}</h3>
        <p class="section-description">{{ $t('shiftCalendar.addWorkers') }}</p>
        
        <div class="worker-selection">
          <div class="available-workers">
            <h4>{{ $t('addWorkers.selectWorkers') }}</h4>
            <div v-if="loadingWorkers" class="loading-state">
              <div class="loading-spinner"></div>
              <span>{{ $t('common.loading') }}</span>
            </div>
            
            <div v-else-if="availableWorkers.length === 0" class="no-workers">
              <span>{{ $t('shiftCreation.noWorkersAvailable') }}</span>
            </div>
            
            <div v-else class="worker-list">
              <div 
                v-for="worker in availableWorkers" 
                :key="worker.id"
                class="worker-item"
                :class="{ 'has-conflict': workerConflicts[worker.id] }"
                @click="toggleWorkerSelection(worker)"
              >
                <div class="worker-info">
                  <span class="worker-name">{{ worker.first_name }} {{ worker.last_name }}</span>
                  <span class="worker-email">{{ worker.email }}</span>
                </div>
                
                <div class="worker-status">
                  <span v-if="isWorkerSelected(worker.id)" class="selected-indicator">✓</span>
                  <span v-if="workerConflicts[worker.id]" class="conflict-indicator" title="Has conflicting shifts">⚠️</span>
                </div>
              </div>
            </div>
          </div>

          <div class="selected-workers">
            <h4>Selected Workers ({{ selectedWorkers.length }})</h4>
            <div v-if="selectedWorkers.length === 0" class="no-selection">
              <span>No workers selected</span>
            </div>
            
            <div v-else class="selected-list">
              <div 
                v-for="worker in selectedWorkers" 
                :key="worker.id"
                class="selected-worker"
              >
                <span class="worker-name">{{ worker.first_name }} {{ worker.last_name }}</span>
                <button 
                  type="button" 
                  @click="removeWorkerSelection(worker.id)"
                  class="remove-btn"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Conflict Warnings -->
        <div v-if="hasConflicts" class="conflict-warnings">
          <h4>⚠️ Conflicts Detected</h4>
          <div 
            v-for="(conflicts, workerId) in workerConflicts" 
            :key="workerId"
            class="conflict-item"
            v-if="conflicts && isWorkerSelected(parseInt(workerId))"
          >
            <span class="worker-name">{{ getWorkerName(parseInt(workerId)) }}</span>
            <div class="conflict-details">
              <div v-for="conflict in conflicts.conflicts" :key="conflict.id" class="conflict-shift">
                {{ formatDateTime(conflict.start_time) }} - {{ formatDateTime(conflict.end_time) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Form Actions -->
      <div class="form-actions">
        <button type="button" @click="closeForm" class="cancel-btn">
          {{ $t('common.cancel') }}
        </button>
        <button type="button" @click="checkConflicts" class="check-btn" :disabled="!canCheckConflicts">
          {{ $t('common.search') }}
        </button>
        <button type="submit" :disabled="saving || !isFormValid" class="save-btn">
          {{ saving ? $t('common.loading') : $t('shiftCreation.createShift') }}
        </button>
      </div>
    </form>

    <!-- Messages -->
    <div v-if="message" :class="['message', messageType]">
      {{ message }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useAuthStore } from '../stores/auth.js'
import { shiftApi } from '../services/shiftApi.js'
import { useShiftStore } from '../stores/shift.js'

// Props and Emits
const props = defineProps({
  preselectedDate: {
    type: String,
    default: ''
  }
})
const emit = defineEmits(['shift-created', 'close'])

// Reactive data
const authStore = useAuthStore()
const saving = ref(false)
const loadingWorkers = ref(false)
const message = ref('')
const messageType = ref('success')
const shiftStore = useShiftStore()
const startTimeInput = ref(null)

const shiftForm = ref({
  date: props.preselectedDate || (() => {
    // Fix: Use local date components to avoid timezone issues
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  })(),
  startTime: '',
  endTime: ''
})

const availableWorkers = ref([])
const selectedWorkerIds = ref([])
const workerConflicts = ref({})

// Computed properties
const today = computed(() => {
  // Fix: Use local date components to avoid timezone issues
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
})

const shiftDuration = computed(() => {
  if (!shiftForm.value.startTime || !shiftForm.value.endTime) return null
  
  const start = new Date(`2000-01-01T${shiftForm.value.startTime}`)
  const end = new Date(`2000-01-01T${shiftForm.value.endTime}`)
  
  if (end <= start) return 'Invalid time range'
  
  const diffMs = end - start
  const hours = Math.floor(diffMs / (1000 * 60 * 60))
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))
  
  return `${hours}h ${minutes}m`
})

const selectedWorkers = computed(() => {
  return availableWorkers.value.filter(worker => selectedWorkerIds.value.includes(worker.id))
})

const hasConflicts = computed(() => {
  return Object.values(workerConflicts.value).some(conflict => conflict && conflict.hasConflicts)
})

const canCheckConflicts = computed(() => {
  return shiftForm.value.date && 
         shiftForm.value.startTime && 
         shiftForm.value.endTime && 
         selectedWorkerIds.value.length > 0 // Only check conflicts if workers are selected
})

const isFormValid = computed(() => {
  return shiftForm.value.date && 
         shiftForm.value.startTime && 
         shiftForm.value.endTime
  // Removed worker requirement - empty shifts are now allowed
})

// Methods
onMounted(() => {
  loadWorkers()
  // Set default date to preselected date or today
  if (!shiftForm.value.date) {
    shiftForm.value.date = props.preselectedDate || today.value
  }
  nextTick(() => {
    if (startTimeInput.value) startTimeInput.value.focus();
  });
})

// Watch for time changes to clear conflicts
watch([() => shiftForm.value.date, () => shiftForm.value.startTime, () => shiftForm.value.endTime], () => {
  workerConflicts.value = {}
})

async function loadWorkers() {
  try {
    loadingWorkers.value = true
    
    // Check if we're authenticated first
    if (!authStore.isAuthenticated) {
      showMessage('Not authenticated. Please login first.', 'error')
      return
    }
    
    // Use the correct API endpoint that matches your Django backend
    const response = await authStore.makeAuthenticatedRequest('/api/users/')
    
    if (response.ok) {
      const workers = await response.json()
      
      if (Array.isArray(workers) && workers.length > 0) {
        availableWorkers.value = workers
        showMessage(`Loaded ${workers.length} workers successfully`, 'success')
      } else {
        availableWorkers.value = []
        showMessage('No workers found in the system', 'info')
      }
    } else {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
  } catch (error) {
    console.error('Error loading workers:', error)
    showMessage('Failed to load workers: ' + error.message, 'error')
    availableWorkers.value = []
  } finally {
    loadingWorkers.value = false
  }
}

function toggleWorkerSelection(worker) {
  const index = selectedWorkerIds.value.indexOf(worker.id)
  if (index > -1) {
    selectedWorkerIds.value.splice(index, 1)
  } else {
    selectedWorkerIds.value.push(worker.id)
  }
}

function isWorkerSelected(workerId) {
  return selectedWorkerIds.value.includes(workerId)
}

function removeWorkerSelection(workerId) {
  const index = selectedWorkerIds.value.indexOf(workerId)
  if (index > -1) {
    selectedWorkerIds.value.splice(index, 1)
  }
}

function getWorkerName(workerId) {
  const worker = availableWorkers.value.find(w => w.id === workerId)
  return worker ? `${worker.first_name} ${worker.last_name}` : 'Unknown Worker'
}

async function checkConflicts() {
  if (!canCheckConflicts.value) return

  const startDateTime = new Date(`${shiftForm.value.date}T${shiftForm.value.startTime}`)
  const endDateTime = new Date(`${shiftForm.value.date}T${shiftForm.value.endTime}`)

  workerConflicts.value = {}

  for (const workerId of selectedWorkerIds.value) {
    try {
      const conflictCheck = await shiftApi.checkWorkerConflicts(
        workerId, 
        startDateTime.toISOString(), 
        endDateTime.toISOString()
      )
      
      if (conflictCheck.hasConflicts) {
        workerConflicts.value[workerId] = conflictCheck
      }
    } catch (error) {
      console.error(`Failed to check conflicts for worker ${workerId}:`, error)
    }
  }

  if (hasConflicts.value) {
    showMessage('Conflicts detected! Review the warnings below.', 'error')
  } else {
    showMessage('No conflicts detected!', 'success')
  }
}

async function createShift() {
  if (!isFormValid.value) return;

  try {
    saving.value = true;

    // Create shift datetime objects
    const startDateTime = new Date(`${shiftForm.value.date}T${shiftForm.value.startTime}`);
    const endDateTime = new Date(`${shiftForm.value.date}T${shiftForm.value.endTime}`);

    // Check if the shift is within 2 weeks
    const today = new Date();
    const twoWeeksFromNow = new Date();
    twoWeeksFromNow.setDate(today.getDate() + 14);

    const isWithinTwoWeeks = startDateTime <= twoWeeksFromNow;

    // Create the shift first (shifts don't have status - only assignments do)
    const shiftData = {
      start_time: startDateTime.toISOString(),
      end_time: endDateTime.toISOString()
    };

    const createdShift = await shiftApi.createShift(shiftData);

    // Notify manager if the shift is pending
    if (isWithinTwoWeeks) {
      showMessage('Shift is within 2 weeks and requires worker approval.', 'warning');
    }

    // Assign selected workers to the shift (only if workers are selected)
    if (selectedWorkerIds.value.length > 0) {
      const assignmentPromises = selectedWorkerIds.value.map(async (workerId) => {
        // If shift is more than 2 weeks away, auto-approve assignment
        if (!isWithinTwoWeeks) {
          // Create assignment with status 'approved'
          return await shiftApi.assignWorkerToShift(createdShift.id, workerId, 'approved');
        } else {
          // Create assignment with status 'pending'
          const assignment = await shiftApi.assignWorkerToShift(createdShift.id, workerId, 'pending');
          // Optionally, notify worker for approval here if needed
          return assignment;
        }
      });
      await Promise.all(assignmentPromises);
      showMessage(`Shift created successfully with ${selectedWorkerIds.value.length} worker(s) assigned!`, 'success');
      shiftStore.notifyShiftsChanged();
    } else {
      showMessage('Empty shift created successfully! You can add workers later.', 'success');
    }

    // Emit success event
    emit('shift-created', {
      shift: createdShift,
      assignedWorkers: selectedWorkers.value
    });

    // Reset form
    resetForm();

  } catch (error) {
    showMessage('Failed to create shift: ' + error.message, 'error');
  } finally {
    saving.value = false;
  }
}

function resetForm() {
  shiftForm.value = {
    date: today.value,
    startTime: '',
    endTime: ''
  }
  selectedWorkerIds.value = []
  workerConflicts.value = {}
}

function closeForm() {
  emit('close')
}

function formatDateTime(dateTimeString) {
  const date = new Date(dateTimeString)
  return date.toLocaleString('sv-SE', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function showMessage(msg, type) {
  message.value = msg
  messageType.value = type
  setTimeout(() => {
    message.value = ''
  }, 5000)
}
</script>

<style scoped>
.shift-creation {
  max-width: 100%;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid rgba(102, 126, 234, 0.1);
}

.header-section h2 {
  color: #333;
  margin: 0;
}

.close-btn {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 1.2rem;
  cursor: pointer;
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: scale(1.05);
}

.shift-form {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.form-section {
  background: rgba(255, 255, 255, 0.5);
  padding-top: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.form-section h3 {
  margin: 0 0 0.5rem 0;
  color: #4a5568;
  font-size: 1.1rem;
}

.section-description {
  margin: 0 0 1rem 0;
  color: #6b7280;
  font-size: 0.9rem;
  font-style: italic;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-row:only-child,
.form-row:last-child {
  margin-bottom: 0;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  font-weight: 600;
  color: #374151;
}

.form-input {
  padding: 0.75rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s ease;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
}

.duration-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
  margin-top: 1rem;
}

.duration-label {
  font-weight: 600;
  color: #4a5568;
}

.duration-value {
  font-weight: 700;
  color: #667eea;
}

.worker-selection {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.worker-selection h4 {
  margin: 0 0 1rem 0;
  color: #4a5568;
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid #e5e7eb;
  border-top: 2px solid #667eea;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-workers,
.no-selection {
  text-align: center;
  color: #6b7280;
  padding: 2rem;
  background: rgba(243, 244, 246, 0.5);
  border-radius: 8px;
}

.worker-list {
  max-height: 300px;
  overflow-y: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
}

.worker-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}

.worker-item:hover {
  background: rgba(102, 126, 234, 0.05);
}

.worker-item:last-child {
  border-bottom: none;
}

.worker-item.has-conflict {
  background: rgba(239, 68, 68, 0.05);
  border-left: 4px solid #ef4444;
}

.worker-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.worker-name {
  font-weight: 600;
  color: #374151;
}

.worker-email {
  font-size: 0.875rem;
  color: #6b7280;
}

.worker-status {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.selected-indicator {
  color: #10b981;
  font-weight: bold;
  font-size: 1.2rem;
}

.conflict-indicator {
  color: #ef4444;
  font-size: 1.2rem;
}

.selected-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selected-worker {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 8px;
}

.remove-btn {
  background: rgba(239, 68, 68, 0.1);
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  color: #ef4444;
  font-weight: bold;
  transition: all 0.2s ease;
}

.remove-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.conflict-warnings {
  margin-top: 1rem;
  padding: 1rem;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 8px;
}

.conflict-warnings h4 {
  margin: 0 0 1rem 0;
  color: #dc2626;
}

.conflict-item {
  margin-bottom: 1rem;
}

.conflict-item:last-child {
  margin-bottom: 0;
}

.conflict-details {
  margin-top: 0.5rem;
  font-size: 0.875rem;
  color: #6b7280;
}

.conflict-shift {
  padding: 0.25rem 0;
}

.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 2px solid rgba(102, 126, 234, 0.1);
}

.cancel-btn {
  padding: 0.75rem 1.5rem;
  background: #e5e7eb;
  color: #374151;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.cancel-btn:hover {
  background: #d1d5db;
}

.check-btn {
  padding: 0.75rem 1.5rem;
  background: #f59e0b;
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
}

.check-btn:hover:not(:disabled) {
  background: #d97706;
}

.check-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-btn {
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
}

.save-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
}

.save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.message {
  position: fixed;
  top: 1rem;
  right: 1rem;
  padding: 1rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  z-index: 1001;
  max-width: 400px;
}

.message.success {
  background: #d1fae5;
  color: #065f46;
  border: 1px solid #a7f3d0;
}

.message.error {
  background: #fee2e2;
  color: #991b1b;
  border: 1px solid #fecaca;
}

.message.info {
  background: #dbeafe;
  color: #1e40af;
  border: 1px solid #93c5fd;
}

.message.warning {
  background: #fff3cd;
  color: #856404;
  border: 1px solid #ffeeba;
}

/* Responsive */
@media (max-width: 768px) {
  .shift-creation {
    margin: 1rem;
    padding: 1rem;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .worker-selection {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
  }
}
</style>